import React, { useState, useCallback, memo } from 'react';
import {
    TextField, Button, Box, Typography,
    InputAdornment, IconButton, Alert,
    Paper, useTheme, useMediaQuery, alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    LockOutlined,
    Visibility,
    VisibilityOff,
    EmailOutlined,
    AdminPanelSettings
} from '@mui/icons-material';
import api from '../utils/Api';
import logo from '../assets/school_logo.png';

// 🎯 Memoized Form Components
const AuthTextField = memo(({
    label,
    name,
    type = 'text',
    value,
    onChange,
    onKeyPress,
    showPassword,
    onTogglePassword,
    size,
    theme,
    isMobile,
    startIcon: StartIcon,
    error,
    helperText
}) => (
    <TextField
        fullWidth
        margin="normal"
        variant="outlined"
        label={label}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onKeyPress={onKeyPress}
        size={size}
        error={error}
        helperText={helperText}
        InputProps={{
            startAdornment: StartIcon && (
                <InputAdornment position="start">
                    <StartIcon sx={{
                        color: error ? 'error.main' : 'primary.main',
                        fontSize: isMobile ? '1.2rem' : '1.5rem'
                    }} />
                </InputAdornment>
            ),
            endAdornment: onTogglePassword && (
                <InputAdornment position="end">
                    <IconButton
                        onClick={onTogglePassword}
                        edge="end"
                        size={size}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'primary.main',
                                bgcolor: alpha(theme.palette.primary.main, 0.1)
                            }
                        }}
                    >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                </InputAdornment>
            )
        }}
        sx={{
            '& .MuiOutlinedInput-root': {
                borderRadius: isMobile ? 2 : 3,
                fontSize: isMobile ? '0.9rem' : '1rem',
                transition: 'all 0.2s ease-in-out',
                '&:hover fieldset': {
                    borderColor: error ? 'error.main' : 'primary.main',
                    borderWidth: '2px'
                },
                '&.Mui-focused fieldset': {
                    borderColor: error ? 'error.main' : 'primary.main',
                    borderWidth: '2px',
                    boxShadow: error
                        ? '0 0 0 4px rgba(244, 67, 54, 0.1)'
                        : '0 0 0 4px rgba(102, 126, 234, 0.1)'
                },
                '& fieldset': {
                    borderWidth: '2px',
                    borderColor: error
                        ? alpha(theme.palette.error.main, 0.3)
                        : alpha(theme.palette.primary.main, 0.2)
                }
            },
            '& .MuiInputLabel-root': {
                fontWeight: 600,
                fontSize: isMobile ? '0.9rem' : '1rem'
            },
            '& .MuiFormHelperText-root': {
                fontSize: isMobile ? '0.75rem' : '0.875rem'
            }
        }}
    />
));

const SuperAdminLoginPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const [form, setForm] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    }, [error]);

    const handleSubmit = useCallback(async () => {
        try {
            setIsLoading(true);
            setError('');

            const res = await api.post('/auth/login', form);
            const { user, token } = res.data;

            // 🛡️ Strict Role Check
            if (user.role !== 'superadmin') {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                await api.post('/auth/logout');
                setError('Access denied: unexpected role. Only Super Admins allowed.');
                setIsLoading(false);
                return;
            }

            // Store credentials
            localStorage.setItem('user', JSON.stringify(user));
            if (token) localStorage.setItem('token', token);

            // Redirect to dedicated dashboard
            navigate('/super-admin/dashboard');
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed. Please verify credentials.');
        } finally {
            setIsLoading(false);
        }
    }, [form, navigate]);

    return (
        <Box sx={{
            minHeight: '100vh',
            width: '100%',
            // Enhanced Background: Deep rich gradient with mesh effect
            background: '#0f172a',
            backgroundImage: `
              radial-gradient(at 0% 0%, hsla(253,16%,7%,1) 0, transparent 50%), 
              radial-gradient(at 50% 0%, hsla(225,39%,30%,1) 0, transparent 50%), 
              radial-gradient(at 100% 0%, hsla(339,49%,30%,1) 0, transparent 50%)
            `,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            py: isMobile ? 2 : 0,
            px: isMobile ? 1 : 0,
            // Subtle grid pattern overlay
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
                backgroundSize: '50px 50px',
                zIndex: 0,
                pointerEvents: 'none'
            }
        }}>
            {/* 🔮 Animated Gradient Orbs */}
            <Box sx={{
                position: 'absolute',
                top: '-15%',
                left: '-15%',
                width: isMobile ? '300px' : '600px',
                height: isMobile ? '300px' : '600px',
                background: 'radial-gradient(circle, rgba(102, 126, 234, 0.4) 0%, rgba(102, 126, 234, 0) 70%)',
                borderRadius: '50%',
                filter: 'blur(80px)',
                animation: 'floatBlob1 20s infinite alternate',
                zIndex: 0,
                '@keyframes floatBlob1': {
                    '0%': { transform: 'translate(0, 0) scale(1)' },
                    '100%': { transform: 'translate(50px, 50px) scale(1.1)' }
                }
            }} />

            <Box sx={{
                position: 'absolute',
                bottom: '-15%',
                right: '-15%',
                width: isMobile ? '300px' : '500px',
                height: isMobile ? '300px' : '500px',
                background: 'radial-gradient(circle, rgba(236, 72, 153, 0.3) 0%, rgba(236, 72, 153, 0) 70%)',
                borderRadius: '50%',
                filter: 'blur(80px)',
                animation: 'floatBlob2 25s infinite alternate-reverse',
                zIndex: 0,
                '@keyframes floatBlob2': {
                    '0%': { transform: 'translate(0, 0) scale(1)' },
                    '100%': { transform: 'translate(-50px, -50px) scale(1.1)' }
                }
            }} />

            <Paper elevation={24} sx={{
                maxWidth: 450,
                width: '100%',
                p: isMobile ? 3 : 5,
                borderRadius: isMobile ? 3 : 5,
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                background: theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.85)' : 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(24px)',
                border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(255, 255, 255, 0.2)',
                position: 'relative',
                zIndex: 1,
                overflow: 'hidden',
                textAlign: 'center',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
                }
            }}>
                <Box
                    component="img"
                    src={logo}
                    alt="School Logo"
                    sx={{
                        width: isMobile ? 80 : 100,
                        height: isMobile ? 80 : 100,
                        objectFit: 'contain',
                        mb: isMobile ? 2 : 3,
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                    }}
                />

                <Typography variant="h4" fontWeight="800" gutterBottom color="primary.dark" sx={{
                    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}>
                    Super Admin
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Restricted Access Portal
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                <AuthTextField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    theme={theme}
                    isMobile={isMobile}
                    startIcon={EmailOutlined}
                />

                <AuthTextField
                    label="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={handleChange}
                    theme={theme}
                    isMobile={isMobile}
                    startIcon={LockOutlined}
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword(!showPassword)}
                />

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    sx={{
                        mt: 3,
                        py: 1.5,
                        borderRadius: 3,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        boxShadow: '0 8px 25px rgba(13, 71, 161, 0.4)',
                        background: 'linear-gradient(45deg, #0d47a1 30%, #1976d2 90%)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #002171 30%, #0d47a1 90%)',
                            transform: 'translateY(-2px)',
                        }
                    }}
                >
                    {isLoading ? 'Verifying...' : 'Access Dashboard'}
                </Button>


                {/* Global Styles for Animations */}
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
            </Paper>
        </Box>

    );
};

export default SuperAdminLoginPage;
